/* <cart-drawer> — the X-All cart drawer (design system CartDrawer, D2 · Switch,
   promoted 2026-09-08). Map: docs/design-maps/cart-drawer.md.

   Keeps Dawn's element name and contract — getSectionsToRender(),
   renderContents(parsedState), setActiveElement(el) — so product-form.js and
   quick-add.js land every add in this drawer unchanged. Everything the drawer
   shows is rendered by sections/xall-cart-drawer.liquid; this script only
   moves the cart (Shopify Ajax API) and swaps the re-rendered section in:

   - quantity: /cart/change.js {id: line key, quantity}
   - mode: /cart/change.js {id, quantity, selling_plan} — the plan id comes
     from the line's data attribute (resolved by xall-offer); '' makes the
     line one-time. Shopify reprices the line itself; no arithmetic here.
   - remove: quantity 0, with an in-place undo row for 8s (timer pauses while
     the pointer or keyboard is inside it). Undo re-adds variant + plan + qty.
   - switch all: one change per switchable line, then one re-render.
   - caps are enforced by the disabled stepper buttons the section renders and
     re-checked here before a request goes out.
   - the arrival line is today + dispatch + transit business days, formatted
     en-US short month, written into [data-x-arrival] from the section's copy.

   Without JS: the header bag is a link to /cart and nothing here renders. */
(() => {
  if (customElements.get('cart-drawer')) return;

  const UNDO_MS = 8000;
  const SECTION = 'xall-cart-drawer';

  const addBusinessDays = (date, n) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let left = Math.max(0, n | 0);
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) left -= 1;
    }
    return d;
  };

  class XallCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.xReady) return;
      this.dataset.xReady = '1';
      this.panel = this.querySelector('#x-kd-panel');
      this.busy = false;
      this.gone = null;
      this.undoTimer = null;
      this.undoHeld = false;
      this.activeElement = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      this.querySelector('[data-x-notice]')?.addEventListener('mouseenter', () => { this.undoHeld = true; });
      this.querySelector('[data-x-notice]')?.addEventListener('mouseleave', () => { this.undoHeld = false; this.armUndo(); });
      this.querySelector('[data-x-notice]')?.addEventListener('focusin', () => { this.undoHeld = true; });
      this.querySelector('[data-x-notice]')?.addEventListener('focusout', () => { this.undoHeld = false; this.armUndo(); });

      this.wireBag();
      this.renderArrival();
    }

    /* ── Dawn contract ── */
    getSectionsToRender() {
      return [
        { id: SECTION, section: SECTION, selector: '#x-kd-panel' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
      ];
    }

    setActiveElement(el) { this.activeElement = el; }

    /* Called by product-form / quick-add after an add: swap the section in and open. */
    renderContents(parsedState) {
      this.paint(parsedState.sections || {});
      this.clearUndo();
      this.open();
      this.enforceCaps();
    }

    /* Adds merge into an existing line (same variant + plan), so a PDP or card add can push a
       line past its policy cap. The section flags such a row (data-x-over); trim it to the cap
       and say so — the only place that sees the whole cart is here. */
    async enforceCaps() {
      const over = this.panel.querySelector('[data-x-key][data-x-over]');
      if (!over || this.busy) return;
      const cap = parseInt(over.dataset.xCap, 10);
      if (!cap) return;
      this.setBusy(true);
      try {
        const state = await this.post('/cart/change.js', { id: over.dataset.xKey, quantity: cap, ...this.sectionsBody() });
        this.afterChange(state);
        if (cap <= 12) this.notice((this.dataset.capNote || 'Limit [cap] per order').replace('[cap]', cap), 'info');
      } catch (err) {
        this.notice(err.message);
      } finally {
        this.setBusy(false);
      }
    }

    /* ── DOM swap ── */
    paint(sections) {
      const html = sections[SECTION];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('cart-drawer');
        const panel = doc.querySelector('#x-kd-panel');
        if (next && panel) {
          this.classList.toggle('is-empty', next.classList.contains('is-empty'));
          Object.keys(next.dataset).forEach((k) => { if (k !== 'xReady') this.dataset[k] = next.dataset[k]; });
          const focusKey = document.activeElement?.closest?.('[data-x-key]')?.dataset.xKey;
          const focusSel = document.activeElement?.dataset?.xQty != null ? `[data-x-qty="${document.activeElement.dataset.xQty}"]`
            : document.activeElement?.hasAttribute?.('data-x-mode') ? '[data-x-mode]' : null;
          this.panel.innerHTML = panel.innerHTML;
          this.renderArrival();
          if (focusKey && focusSel) {
            const row = this.panel.querySelector(`[data-x-key="${focusKey}"]`);
            const el = row?.querySelector(focusSel);
            if (el && !el.disabled) el.focus(); else row?.querySelector('[data-x-remove]')?.focus();
          }
          // never strand keyboard focus on the body after a repaint
          if (this.classList.contains('is-open') && !this.panel.contains(document.activeElement)) this.panel.focus();
        }
      }
      const bubbleHtml = sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const inner = doc.querySelector('.shopify-section') || doc.body;
        bubble.innerHTML = inner.innerHTML;
      }
    }

    renderArrival() {
      const el = this.panel.querySelector('[data-x-arrival]');
      if (!el) return;
      const copy = this.dataset.arrivalCopy || '';
      if (!copy.trim()) { el.hidden = true; return; }
      const days = (parseInt(this.dataset.dispatchDays, 10) || 0) + (parseInt(this.dataset.transitMax, 10) || 0);
      const date = addBusinessDays(new Date(), days).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      el.textContent = copy.replace('[date]', date);
      el.hidden = false;
    }

    /* ── open / close ── */
    wireBag() {
      const bag = document.getElementById('cart-icon-bubble');
      if (!bag || bag.dataset.xDrawer) return;
      bag.dataset.xDrawer = '1';
      bag.setAttribute('role', 'button');
      bag.setAttribute('aria-haspopup', 'dialog');
      bag.addEventListener('click', (e) => { e.preventDefault(); this.open(bag); });
      bag.addEventListener('keydown', (e) => { if (e.code === 'Space') { e.preventDefault(); this.open(bag); } });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      if (this.classList.contains('is-open')) return;
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      const focusTo = this.panel.querySelector('.x-kd__x') || this.panel;
      if (typeof trapFocus === 'function') trapFocus(this.panel, focusTo); else focusTo.focus();
      this.enforceCaps();
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      this.clearUndo();
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement); else this.activeElement?.focus?.();
    }

    /* ── actions ── */
    onClick(e) {
      const t = e.target;
      if (t.closest('[data-x-close]')) { this.close(); return; }
      if (t.closest('[data-x-undo]')) { this.undo(); return; }
      if (this.busy) return;
      const qty = t.closest('[data-x-qty]');
      if (qty) { this.step(qty.closest('[data-x-key]'), parseInt(qty.dataset.xQty, 10)); return; }
      const mode = t.closest('[data-x-mode]');
      if (mode) { this.switchMode(mode.closest('[data-x-key]'), mode); return; }
      const rm = t.closest('[data-x-remove]');
      if (rm) { this.remove(rm.closest('[data-x-key]'), rm); return; }
      if (t.closest('[data-x-switch-all]')) { this.switchAll(); }
    }

    caps() {
      return { sub: parseInt(this.dataset.capSub, 10) || 99, once: parseInt(this.dataset.capOnce, 10) || 6 };
    }

    step(row, delta) {
      if (!row) return;
      const current = parseInt(row.querySelector('[data-x-qtybox] span')?.textContent, 10) || 1;
      const cap = row.dataset.xSub === '1' ? this.caps().sub : this.caps().once;
      const next = Math.min(cap, Math.max(1, current + delta));
      if (next === current) return;
      this.clearUndo();
      this.change({ id: row.dataset.xKey, quantity: next });
    }

    switchMode(row, btn) {
      if (!row) return;
      const toSub = btn.getAttribute('aria-pressed') !== 'true';
      const plan = btn.dataset.xPlan || '';
      if (toSub && !plan) return; // no resolvable plan: the section would not have rendered a switch
      const current = parseInt(row.querySelector('[data-x-qtybox] span')?.textContent, 10) || 1;
      const cap = toSub ? this.caps().sub : this.caps().once;
      this.clearUndo();
      this.change({ id: row.dataset.xKey, quantity: Math.min(cap, current), selling_plan: toSub ? plan : '' });
    }

    async switchAll() {
      const rows = [...this.panel.querySelectorAll('[data-x-key][data-x-sub="0"]')]
        .map((row) => ({ row, btn: row.querySelector('[data-x-mode]') }))
        .filter(({ btn }) => btn && btn.dataset.xPlan);
      if (!rows.length) return;
      this.clearUndo();
      this.setBusy(true);
      try {
        for (const { row, btn } of rows) {
          const current = parseInt(row.querySelector('[data-x-qtybox] span')?.textContent, 10) || 1;
          await this.post('/cart/change.js', { id: row.dataset.xKey, quantity: Math.min(this.caps().sub, current), selling_plan: btn.dataset.xPlan });
        }
        const state = await this.post('/cart/update.js', { updates: {}, ...this.sectionsBody() });
        this.afterChange(state);
      } catch (err) {
        this.notice(err.message);
      } finally {
        this.setBusy(false);
      }
    }

    remove(row, btn) {
      if (!row) return;
      this.gone = {
        name: btn.dataset.xName,
        variant: btn.dataset.xVariant,
        qty: parseInt(btn.dataset.xQtyval, 10) || 1,
        plan: btn.dataset.xPlanid || '',
      };
      this.change({ id: row.dataset.xKey, quantity: 0 }, () => this.showUndo());
    }

    async undo() {
      const g = this.gone;
      if (!g) return;
      this.clearUndo();
      this.setBusy(true);
      try {
        const body = { items: [{ id: parseInt(g.variant, 10), quantity: g.qty }], ...this.sectionsBody() };
        if (g.plan) body.items[0].selling_plan = parseInt(g.plan, 10);
        const state = await this.post('/cart/add.js', body);
        this.afterChange(state);
      } catch (err) {
        this.notice(err.message);
      } finally {
        this.setBusy(false);
      }
    }

    /* ── transport ── */
    sectionsBody() {
      return { sections: this.getSectionsToRender().map((s) => s.section).join(','), sections_url: window.location.pathname };
    }

    async post(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.status) throw new Error(json.description || json.message || window.cartStrings?.error || 'Something went wrong');
      return json;
    }

    async change(body, then) {
      this.setBusy(true);
      try {
        const state = await this.post('/cart/change.js', { ...body, ...this.sectionsBody() });
        this.afterChange(state);
        if (then) then();
      } catch (err) {
        this.gone = null;
        this.notice(err.message);
      } finally {
        this.setBusy(false);
      }
    }

    afterChange(state) {
      this.paint(state.sections || {});
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: state });
      }
    }

    setBusy(on) {
      this.busy = on;
      this.classList.toggle('is-busy', on);
      this.panel.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    /* ── notice + undo ── */
    notice(text, kind = 'error') {
      const slot = this.querySelector('[data-x-notice]');
      if (!slot) return;
      slot.textContent = '';
      if (!text) return;
      const p = document.createElement('div');
      p.className = 'x-kd__undo' + (kind === 'error' ? ' x-kd__undo--error' : '');
      p.textContent = text;
      slot.appendChild(p);
    }

    showUndo() {
      const slot = this.querySelector('[data-x-notice]');
      if (!slot || !this.gone) return;
      slot.textContent = '';
      const wrap = document.createElement('div');
      wrap.className = 'x-kd__undo';
      const span = document.createElement('span');
      span.textContent = (this.dataset.removedCopy || '[name] removed').replace('[name]', this.gone.name || '');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('data-x-undo', '');
      btn.textContent = this.dataset.undoLabel || 'Undo';
      wrap.append(span, btn);
      slot.appendChild(wrap);
      this.armUndo();
    }

    armUndo() {
      clearTimeout(this.undoTimer);
      if (!this.gone || this.undoHeld) return;
      this.undoTimer = setTimeout(() => this.clearUndo(), UNDO_MS);
    }

    clearUndo() {
      clearTimeout(this.undoTimer);
      this.gone = null;
      const slot = this.querySelector('[data-x-notice]');
      if (slot) slot.textContent = '';
    }
  }

  customElements.define('cart-drawer', XallCartDrawer);
})();
